import { Vector2 } from 'three';
import {
  BlendFunction,
  BloomEffect,
  ChromaticAberrationEffect,
  EffectComposer,
  EffectPass,
  RenderPass,
  SMAAEffect,
  ToneMappingEffect,
  ToneMappingMode,
  VignetteEffect,
} from 'postprocessing';
import { N8AOPostPass } from 'n8ao';

let composer = null;
let renderPass = null;
let aoPass = null;
let effectPass = null;
let chromaticPass = null;
let effects = null;
let pixelRatio = 1;

export function initPostprocessing(renderer, scene, camera, options = {}) {
  const {
    bloomEnabled = true,
    bloomStrength = 1.1,
    bloomThreshold = 0.5,
    initialPixelRatio = window.devicePixelRatio || 1,
  } = options;

  pixelRatio = initialPixelRatio;
  composer = new EffectComposer(renderer);
  renderPass = new RenderPass(scene, camera);
  aoPass = new N8AOPostPass(scene, camera, window.innerWidth || 1, window.innerHeight || 1);
  effects = createEffects({ bloomEnabled, bloomStrength, bloomThreshold });
  effectPass = new EffectPass(
    camera,
    effects.smaa,
    effects.bloom,
    effects.vignette,
    effects.toneMapping,
  );
  chromaticPass = new EffectPass(camera, effects.chromatic);

  composer.addPass(renderPass);
  composer.addPass(aoPass);
  composer.addPass(effectPass);
  composer.addPass(chromaticPass);
  setPostprocessingQuality({ tier: bloomEnabled ? 'high' : 'low', bloom: bloomEnabled });
  resizePostprocessing(window.innerWidth || 1, window.innerHeight || 1, pixelRatio);
  window.addEventListener('resize', handleWindowResize);

  return { composer, renderPass, aoPass, effectPass, chromaticPass, effects };
}

export function getComposer() {
  if (!composer) {
    throw new Error('[postprocessing] initPostprocessing() must run before getComposer().');
  }
  return composer;
}

export function setBloomQuality(options = {}) {
  setPostprocessingQuality({
    tier: options.enabled ? 'high' : 'low',
    bloom: options.enabled,
    bloomStrength: options.strength,
    bloomThreshold: options.threshold,
  });
}

export function setPostprocessingQuality(options = {}) {
  if (!effects) return;
  const tier = options.tier || 'low';
  const bloomEnabled = !!options.bloom;
  const highTier = tier === 'high' || tier === 'ultra';

  effects.bloom.intensity = options.bloomStrength ?? (bloomEnabled ? 0.7 : 0);
  effects.bloom.luminanceMaterial.threshold = options.bloomThreshold ?? 0.85;
  effects.bloom.blendMode.opacity.value = bloomEnabled ? 1 : 0;
  effects.chromatic.blendMode.opacity.value = highTier ? 1 : tier === 'medium' ? 0.35 : 0;
  effects.vignette.blendMode.opacity.value = tier === 'low' ? 0.55 : 1;
  effects.smaa.blendMode.opacity.value = highTier ? 1 : 0;

  if (aoPass) {
    aoPass.enabled = highTier;
    Object.assign(aoPass.configuration, {
      aoSamples: tier === 'ultra' ? 16 : 8,
      denoiseSamples: tier === 'ultra' ? 8 : 4,
      aoRadius: tier === 'ultra' ? 7 : 5,
      intensity: tier === 'ultra' ? 1.35 : 1.05,
      distanceFalloff: 0.7,
    });
  }
}

export function resizePostprocessing(width, height, nextPixelRatio = pixelRatio) {
  if (!composer) return;

  pixelRatio = nextPixelRatio;
  composer.setPixelRatio?.(pixelRatio);
  composer.setSize(width, height);
  aoPass?.setSize?.(width * pixelRatio, height * pixelRatio);
}

export function renderPostprocessing(dt) {
  getComposer().render(dt);
}

function createEffects({ bloomEnabled, bloomStrength, bloomThreshold }) {
  const bloom = new BloomEffect({
    blendFunction: BlendFunction.SCREEN,
    intensity: bloomEnabled ? bloomStrength : 0.85,
    mipmapBlur: true,
    luminanceThreshold: bloomThreshold,
    luminanceSmoothing: 0.08,
    radius: 0.75,
    levels: 8,
  });
  return {
    bloom,
    chromatic: new ChromaticAberrationEffect({
      blendFunction: BlendFunction.NORMAL,
      offset: new Vector2(0.0006, 0.0002),
    }),
    vignette: new VignetteEffect({
      blendFunction: BlendFunction.NORMAL,
      offset: 0.4,
      darkness: 0.6,
    }),
    smaa: new SMAAEffect(),
    toneMapping: new ToneMappingEffect({ mode: ToneMappingMode.ACES_FILMIC }),
  };
}

function handleWindowResize() {
  resizePostprocessing(window.innerWidth || 1, window.innerHeight || 1, pixelRatio);
}

export function disposePostprocessing() {
  window.removeEventListener('resize', handleWindowResize);
  composer?.dispose?.();
  composer = null;
  renderPass = null;
  aoPass = null;
  effectPass = null;
  chromaticPass = null;
  effects = null;
}
